import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useFetch } from "../../lib/useFetch";
import { useAntiBot, useSeo, getConsent, setConsent } from "../../lib/hooks";
import { api, errMsg, fmtDate } from "../../lib/api";
import { PageHero } from "../../components/site/SiteLayout";
import { OsmMap } from "../../components/site/CookieBanner";
import { Field, RevealContact, inputCls, btnPrimary } from "../../components/site/ui";

const MONTHS = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export function Scadenze() {
  useSeo("Scadenze", "Calendario delle principali scadenze pubblicato e validato dallo studio.");
  const d = new Date();
  const [ym, setYm] = useState([d.getFullYear(), d.getMonth() + 1]);
  const { data } = useFetch(`/public/deadlines?year=${ym[0]}&month=${ym[1]}`);
  const move = (k) => setYm(([y, m]) => (m + k < 1 ? [y - 1, 12] : m + k > 12 ? [y + 1, 1] : [y, m + k]));
  return (
    <>
      <PageHero eyebrow="Calendario" title="Scadenze">Le scadenze sono inserite e verificate dagli operatori dello studio. Per la propria situazione specifica fa fede quanto comunicato nell'area clienti.</PageHero>
      <section className="section container-x max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => move(-1)} className="tap min-w-[44px] grid place-items-center border rounded-md" aria-label="Mese precedente" data-testid="deadlines-prev"><ChevronLeft /></button>
          <h2 className="text-2xl sm:text-3xl font-semibold" aria-live="polite" data-testid="deadlines-month">{MONTHS[ym[1] - 1]} {ym[0]}</h2>
          <button onClick={() => move(1)} className="tap min-w-[44px] grid place-items-center border rounded-md" aria-label="Mese successivo" data-testid="deadlines-next"><ChevronRight /></button>
        </div>
        {data && data.length === 0 && <p className="text-muted-foreground card-line p-6">Nessuna scadenza pubblicata per questo mese.</p>}
        <ul className="space-y-3">
          {(data || []).map((x) => (
            <li key={x.id} className="card-line p-5 flex gap-5" data-testid={`deadline-${x.id}`}>
              <div className="text-center min-w-[64px]"><p className="font-display text-3xl font-semibold">{x.date.slice(8)}</p><p className="text-xs uppercase text-muted-foreground">{MONTHS[+x.date.slice(5, 7) - 1].slice(0, 3)}</p></div>
              <div><p className="eyebrow">{x.category}</p><h3 className="text-lg font-semibold">{x.title}</h3>{x.description && <p className="text-sm text-muted-foreground mt-1">{x.description}</p>}</div>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}

export function Faq() {
  useSeo("Domande frequenti", "Consegna documenti, accesso all'area clienti, appuntamenti e contatti: le risposte alle domande più comuni.");
  const items = useFetch("/public/faq").data || [];
  return (
    <>
      <PageHero eyebrow="FAQ" title="Domande frequenti" />
      <section className="section container-x max-w-3xl">
        <div className="divide-y border-y">
          {items.map((f) => (
            <details key={f.id} className="group py-2" data-testid={`faq-${f.id}`}>
              <summary className="tap flex items-center justify-between cursor-pointer py-3 text-lg font-semibold list-none">{f.question}<span aria-hidden className="ml-4 text-[hsl(var(--gold-text))] group-open:rotate-45 transition-transform">+</span></summary>
              <p className="pb-4 text-muted-foreground leading-relaxed whitespace-pre-line">{f.answer}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}

function useForm(initial) {
  const [v, setV] = useState(initial);
  const bind = (k) => ({ id: k, value: v[k], onChange: (e) => setV({ ...v, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }) });
  return [v, setV, bind];
}

function PrivacyAck({ bind }) {
  return (
    <label className="flex items-start gap-3 text-sm"><input type="checkbox" className="mt-1 h-5 w-5" {...bind("privacy_ack")} checked={bind("privacy_ack").value} data-testid="privacy-ack-checkbox" />
      <span>Ho letto l'<Link to="/privacy" className="underline">informativa privacy</Link>. *</span></label>
  );
}

export function Contatti() {
  useSeo("Contatti", "Contatta lo Studio Diolosa – Commercialisti, Via Cavour 9, Spilimbergo (PN).");
  const site = useFetch("/public/site").data || {};
  const ab = useAntiBot();
  const [v, setV, bind] = useForm({ name: "", email: "", subject: "", message: "", privacy_ack: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await api.post("/public/contact", { ...v, ...(await ab.payload()) }); setSent(true); setV({ name: "", email: "", subject: "", message: "", privacy_ack: false }); }
    catch (x) { setErr(errMsg(x)); }
    setBusy(false);
  };
  return (
    <>
      <PageHero eyebrow="Contatti" title="Contatta lo studio" />
      <section className="section container-x grid gap-12 lg:grid-cols-2">
        <div className="space-y-8">
          <div><h2 className="text-2xl font-semibold">Sede</h2><address className="not-italic mt-2 text-lg">Via Cavour 9<br />33097 Spilimbergo (Pordenone), Italia</address></div>
          <div className="space-y-3"><h2 className="text-2xl font-semibold">Recapiti</h2>
            <p className="text-sm text-muted-foreground">Per ridurre lo spam, i recapiti vengono mostrati su richiesta.</p>
            <div className="flex flex-wrap gap-3"><RevealContact kind="email" /><RevealContact kind="phone" /></div>
          </div>
          {site.opening_hours && <div><h2 className="text-2xl font-semibold">Orari</h2><p className="mt-2 whitespace-pre-line">{site.opening_hours}</p></div>}
          <div><h2 className="text-2xl font-semibold mb-3">Come raggiungerci</h2><OsmMap lat={site.map_lat} lon={site.map_lon} /></div>
        </div>
        <div className="card-line p-6 sm:p-8">
          <h2 className="text-2xl font-semibold">Scrivici</h2>
          <p className="text-sm text-muted-foreground mt-2">Usa questo modulo solo per richieste generiche. <strong>Non inviare documenti fiscali o dati riservati</strong>: per questo usa l'<Link to="/accedi" className="underline">area clienti</Link>.</p>
          {sent ? <p role="status" className="mt-6 p-4 bg-secondary rounded-md" data-testid="contact-success">Grazie, il messaggio è stato inviato. Lo studio ti risponderà appena possibile.</p> : (
            <form onSubmit={submit} className="mt-6 space-y-4 relative" noValidate>
              {ab.field}
              <Field id="name" label="Nome e cognome" required><input className={inputCls} required autoComplete="name" {...bind("name")} data-testid="contact-name-input" /></Field>
              <Field id="email" label="Email" required><input type="email" className={inputCls} required autoComplete="email" {...bind("email")} data-testid="contact-email-input" /></Field>
              <Field id="subject" label="Oggetto"><input className={inputCls} {...bind("subject")} data-testid="contact-subject-input" /></Field>
              <Field id="message" label="Messaggio" required hint="Massimo 3000 caratteri."><textarea rows={5} maxLength={3000} className={inputCls} required {...bind("message")} data-testid="contact-message-input" /></Field>
              <PrivacyAck bind={bind} />
              {err && <p role="alert" className="text-sm text-destructive" data-testid="contact-error">{err}</p>}
              <button className={btnPrimary} disabled={busy} data-testid="contact-submit-btn">{busy ? "Invio…" : "Invia messaggio"}</button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}

export function Appuntamento() {
  useSeo("Richiedi un appuntamento", "Richiedi un appuntamento con lo Studio Diolosa – Commercialisti di Spilimbergo.");
  const services = useFetch("/public/services").data || [];
  const ab = useAntiBot();
  const [v, setV, bind] = useForm({ name: "", email: "", phone: "", service: "", notes: "", privacy_ack: false });
  const [slots, setSlots] = useState(["", ""]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { await api.post("/public/appointment", { ...v, preferred_slots: slots.filter(Boolean), ...(await ab.payload()) }); setSent(true); toast.success("Richiesta inviata"); }
    catch (x) { setErr(errMsg(x)); }
    setBusy(false);
  };
  return (
    <>
      <PageHero eyebrow="Appuntamenti" title="Richiedi un appuntamento">Indica il servizio e alcune fasce orarie preferite. L'appuntamento è confermato solo dopo l'accettazione da parte dello studio.</PageHero>
      <section className="section container-x max-w-2xl">
        {sent ? <div role="status" className="card-line p-6" data-testid="appointment-success"><h2 className="text-2xl font-semibold">Richiesta ricevuta</h2><p className="mt-2">Ti abbiamo inviato un'email di riepilogo. <strong>Non è ancora una conferma</strong>: riceverai un nuovo messaggio quando lo studio avrà fissato data e ora.</p></div> : (
          <form onSubmit={submit} className="space-y-5 relative card-line p-6 sm:p-8" noValidate>
            {ab.field}
            <Field id="name" label="Nome e cognome" required><input className={inputCls} autoComplete="name" {...bind("name")} data-testid="appt-name-input" /></Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="email" label="Email" required><input type="email" className={inputCls} autoComplete="email" {...bind("email")} data-testid="appt-email-input" /></Field>
              <Field id="phone" label="Telefono (facoltativo)"><input type="tel" className={inputCls} autoComplete="tel" {...bind("phone")} data-testid="appt-phone-input" /></Field>
            </div>
            <Field id="service" label="Servizio" required><select className={inputCls} {...bind("service")} data-testid="appt-service-select"><option value="">Seleziona…</option>{services.map((s) => <option key={s.id}>{s.title}</option>)}<option>Altro</option></select></Field>
            <fieldset className="space-y-2"><legend className="text-sm font-medium">Fasce orarie preferite *</legend>
              {slots.map((s, i) => <div key={i}><label htmlFor={`slot-${i}`} className="sr-only">Fascia {i + 1}</label><input id={`slot-${i}`} type="datetime-local" className={inputCls} value={s} onChange={(e) => setSlots(slots.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`appt-slot-${i}`} /></div>)}
              {slots.length < 4 && <button type="button" className="text-sm underline tap" onClick={() => setSlots([...slots, ""])}>Aggiungi un'altra fascia</button>}
            </fieldset>
            <Field id="notes" label="Note (facoltative)" hint="Non inserire dati fiscali o informazioni riservate."><textarea rows={3} maxLength={1000} className={inputCls} {...bind("notes")} data-testid="appt-notes-input" /></Field>
            <PrivacyAck bind={bind} />
            {err && <p role="alert" className="text-sm text-destructive" data-testid="appt-error">{err}</p>}
            <button className={btnPrimary} disabled={busy} data-testid="appt-submit-btn">{busy ? "Invio…" : "Invia richiesta"}</button>
          </form>
        )}
      </section>
    </>
  );
}

export function DatiPersonali() {
  useSeo("Richieste sui dati personali");
  const ab = useAntiBot();
  const [v, , bind] = useForm({ type: "", name: "", email: "", message: "" });
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    try { await api.post("/public/data-request", { ...v, ...(await ab.payload()) }); setMsg("Richiesta registrata. Lo studio ti contatterà all'indirizzo indicato."); } catch (x) { setErr(errMsg(x)); }
  };
  return (
    <>
      <PageHero eyebrow="Privacy" title="Richieste relative ai dati personali" />
      <section className="section container-x max-w-2xl">
        {msg ? <p role="status" className="card-line p-6" data-testid="datareq-success">{msg}</p> : (
          <form onSubmit={submit} className="space-y-4 card-line p-6 relative">
            {ab.field}
            <Field id="type" label="Tipo di richiesta" required><select className={inputCls} {...bind("type")} data-testid="datareq-type-select"><option value="">Seleziona…</option>
              {[["accesso", "Accesso"], ["rettifica", "Rettifica"], ["cancellazione", "Cancellazione"], ["limitazione", "Limitazione"], ["opposizione", "Opposizione"], ["portabilita", "Portabilità"]].map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
            <Field id="name" label="Nome e cognome" required><input className={inputCls} {...bind("name")} data-testid="datareq-name-input" /></Field>
            <Field id="email" label="Email" required><input type="email" className={inputCls} {...bind("email")} data-testid="datareq-email-input" /></Field>
            <Field id="message" label="Dettagli (facoltativi)"><textarea rows={3} className={inputCls} {...bind("message")} /></Field>
            {err && <p role="alert" className="text-sm text-destructive">{err}</p>}
            <button className={btnPrimary} data-testid="datareq-submit-btn">Invia richiesta</button>
          </form>
        )}
      </section>
    </>
  );
}

export function Legal({ kind }) {
  const site = useFetch("/public/site").data;
  useSeo(kind === "privacy" ? "Informativa privacy" : "Cookie e preferenze");
  return (
    <>
      <PageHero eyebrow="Informazioni legali" title={kind === "privacy" ? "Informativa privacy" : "Cookie e preferenze"} />
      <section className="section container-x max-w-3xl space-y-6">
        {site && !site.legal_validated && <p className="p-4 border-l-4 border-[hsl(var(--accent))] bg-secondary text-sm" data-testid="legal-draft-notice">Testo in bozza: deve essere validato dallo studio prima della pubblicazione definitiva.</p>}
        <div className="whitespace-pre-line leading-relaxed">{site?.[kind === "privacy" ? "privacy_text" : "cookie_text"]}</div>
        {kind === "cookie" && <CookiePrefs />}
      </section>
    </>
  );
}

function CookiePrefs() {
  const [map, setMap] = useState(!!getConsent()?.map);
  return (
    <div className="card-line p-6 space-y-4" data-testid="cookie-prefs">
      <h2 className="text-2xl font-semibold">Le tue preferenze</h2>
      <p className="text-sm"><strong>Necessari</strong> (sempre attivi): cookie di sessione per l'accesso all'area clienti.</p>
      <label className="flex items-center gap-3"><input type="checkbox" className="h-5 w-5" checked={map} onChange={(e) => setMap(e.target.checked)} data-testid="cookie-map-toggle" /> Mappa interattiva OpenStreetMap (servizio di terze parti)</label>
      <button className={btnPrimary} onClick={() => { setConsent({ map }); toast.success("Preferenze salvate"); }} data-testid="cookie-save-btn">Salva preferenze</button>
    </div>
  );
}

export function GuidaPortale() {
  useSeo("Guida all'area clienti");
  const steps = [
    ["Attiva l'account", "Riceverai dallo studio un'email di invito. Apri il collegamento e scegli una password di almeno 10 caratteri con lettere e numeri."],
    ["Proteggi l'accesso", "In «Sicurezza» attiva la verifica in due passaggi. Consigliamo un'app Authenticator (es. Google Authenticator, Microsoft Authenticator). Conserva i codici di recupero in un luogo sicuro."],
    ["Consulta i documenti", "In «Documenti» trovi i file pubblicati dallo studio. Puoi cercarli, filtrarli, visualizzarli in anteprima e scaricarli, anche più insieme."],
    ["Rispondi alle richieste", "In «Richieste» trovi l'elenco dei documenti che lo studio ti chiede, con la checklist e la scadenza. Se abilitato, puoi caricarli direttamente."],
    ["Leggi le comunicazioni", "Le comunicazioni riservate dello studio sono disponibili solo nell'area clienti. Le email ti avvisano soltanto che c'è una novità."],
  ];
  return (
    <>
      <PageHero eyebrow="Area clienti" title="Guida breve all'uso del portale" />
      <section className="section container-x max-w-3xl">
        <ol className="space-y-6">{steps.map(([t, d], i) => <li key={t} className="flex gap-5"><span className="font-display text-3xl text-[hsl(var(--gold-text))]">{i + 1}</span><div><h2 className="text-xl font-semibold">{t}</h2><p className="text-muted-foreground mt-1">{d}</p></div></li>)}</ol>
        <Link to="/accedi" className={`${btnPrimary} mt-10`}>Accedi all'area clienti</Link>
      </section>
    </>
  );
}

export function NotFound() {
  useSeo("Pagina non trovata", "", true);
  return <div className="container-x section"><h1 className="text-4xl font-semibold">Pagina non trovata</h1><Link to="/" className="underline mt-4 inline-block">Torna alla home</Link></div>;
}

export { fmtDate };
